import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Divider,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  Stack,
} from "@mui/material";
import { Close, Phone, ContentCopy, WhatsApp } from "@mui/icons-material";
import {
  CONTACT_PHONES,
  PRIMARY_PHONE,
  toTelHref,
  toWhatsAppHref,
} from "../../constants/contactPhones";

const ContactModal = ({ open, onClose, property }) => {
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const officeHours = "Monday - Saturday : 10 AM - 6 PM";

  const handleCall = (phone) => window.open(toTelHref(phone));
  const handleCopyNumber = (phone) => {
    navigator.clipboard
      .writeText(phone)
      .then(() => setSnackbar({ open: true, message: "Phone number copied!" }))
      .catch(() => setSnackbar({ open: true, message: "Failed to copy number" }));
  };
  const handleWhatsApp = () => {
    const message = `Hi, I'm interested in ${property?.PropertyName} at ${property?.Address}, ${property?.City}.`;
    window.open(
      `${toWhatsAppHref(PRIMARY_PHONE)}?text=${encodeURIComponent(message)}`,
      "_blank"
    );
  };
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight={600}>
              Contact Agent
            </Typography>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {property?.PropertyName}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Contact Numbers
            </Typography>
            <Stack spacing={1.5}>
              {CONTACT_PHONES.map((phone) => (
                <Box
                  key={phone}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    backgroundColor: "grey.50",
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="primary"
                    component="a"
                    href={toTelHref(phone)}
                    sx={{ textDecoration: "none", color: "primary.main" }}
                  >
                    {phone}
                  </Typography>
                  <IconButton
                    onClick={() => handleCopyNumber(phone)}
                    size="small"
                    sx={{ color: "primary.main" }}
                    title="Copy number"
                  >
                    <ContentCopy />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
            {isMobile && (
              <Button
                variant="contained"
                startIcon={<Phone />}
                onClick={() => handleCall(PRIMARY_PHONE)}
                sx={{
                  flex: 1,
                  py: 1.5,
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": { backgroundColor: "primary.dark" },
                }}
              >
                Call
              </Button>
            )}

            <Button
              variant="contained"
              startIcon={<WhatsApp />}
              onClick={handleWhatsApp}
              sx={{
                flex: 1,
                py: 1.5,
                px: 5,
                backgroundColor: "#25D366",
                color: "white",
                "&:hover": { backgroundColor: "#1ebe57" },
              }}
            >
              WhatsApp
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mt: 3, p: 2, backgroundColor: "grey.50", borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Office Hours
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {officeHours}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactModal;
